import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiGet, apiPost, getApiBase } from '../../services/api';
import { passwordMeetsStrength, passwordStrengthChecks } from '../../utils/passwordStrength';

export default function InviteAcceptPage() {
  const apiBase = getApiBase();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [checkingToken, setCheckingToken] = useState(true);
  const [inviteMeta, setInviteMeta] = useState(null);
  const [passwordFocused, setPasswordFocused] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function validateToken() {
      if (!token) {
        setError('This invitation link is missing a token.');
        setCheckingToken(false);
        return;
      }

      if (!apiBase) {
        setError('Invitation acceptance is unavailable.');
        setCheckingToken(false);
        return;
      }

      try {
        const res = await apiGet(
          `/api/v1/company/team/invite/status?token=${encodeURIComponent(token)}`
        );
        const data = await res.json().catch(() => ({}));

        if (cancelled) return;

        if (res.ok) {
          setInviteMeta(data.data || null);
          setError('');
          setCheckingToken(false);
          return;
        }

        const messages = [];
        if (data.errors) {
          Object.values(data.errors).forEach((errs) => {
            if (Array.isArray(errs)) messages.push(...errs);
            else if (errs) messages.push(String(errs));
          });
        }
        const message = messages.join(' ') || data.message || 'This invitation is invalid.';
        setError(message);
        setCheckingToken(false);
      } catch {
        if (!cancelled) {
          setError('Unable to validate this invitation.');
          setCheckingToken(false);
        }
      }
    }

    validateToken();

    return () => {
      cancelled = true;
    };
  }, [apiBase, token]);

  async function handleSubmit(e) {
    e.preventDefault();
    setFieldErrors({});
    setError('');

    if (!passwordMeetsStrength(password)) {
      setError('Password does not meet the strength requirements.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Password confirmation does not match.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiPost('/api/v1/company/team/invite/accept', {
        token,
        first_name: firstName,
        last_name: lastName,
        password,
        password_confirmation: confirmPassword,
      });
      const data = await res.json().catch(() => ({}));

      if (res.status === 422) {
        const messages = [];
        if (data.errors) {
          setFieldErrors(data.errors);
          Object.values(data.errors).forEach((errs) => {
            if (Array.isArray(errs)) messages.push(...errs);
            else if (errs) messages.push(String(errs));
          });
        }
        setError(messages.join(' ') || data.message || 'Unable to accept invitation.');
        return;
      }

      if (!res.ok) {
        setError(data.message || 'Something went wrong. Please try again.');
        return;
      }

      const user = data?.data?.user ?? null;
      const authToken = data?.data?.token ?? null;
      if (user && authToken) {
        login(user, authToken);
      }
      setSuccess(true);
      navigate('/company/dashboard', { replace: true });
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const checks = passwordStrengthChecks(password);
  const allStrengthMet = passwordMeetsStrength(password);
  const showPasswordRequirements = !allStrengthMet || passwordFocused;

  return (
    <div className="rwm-auth rwm-invite-accept bg-white">
      <div className="rwm-auth__card py-5">
        <Card className="border" style={{ borderColor: 'var(--rwm-border)' }}>
          <Card.Body className="p-4">
            <h1 className="h4 fw-semibold mb-3" style={{ color: 'var(--rwm-dark)' }}>
              Accept invitation
            </h1>

            {checkingToken ? (
              <div className="text-center py-4">
                <Spinner animation="border" role="status" size="sm">
                  <span className="visually-hidden">Loading…</span>
                </Spinner>
              </div>
            ) : null}

            {!checkingToken && error && !inviteMeta ? (
              <Alert variant="danger" data-testid="rwm-invite-accept-error">
                {error}
              </Alert>
            ) : null}

            {!checkingToken && inviteMeta ? (
              <>
                <p className="text-muted mb-4">
                  You&apos;ve been invited to join <strong>{inviteMeta.company_name}</strong> as a{' '}
                  {inviteMeta.role}. Set a password to activate your account.
                </p>

                {success ? (
                  <Alert variant="success" data-testid="rwm-invite-accept-success">
                    Account created. Redirecting…
                  </Alert>
                ) : null}

                {error ? (
                  <Alert variant="danger" data-testid="rwm-invite-accept-error">
                    {error}
                  </Alert>
                ) : null}

                <Form onSubmit={handleSubmit} data-testid="rwm-invite-accept-form">
                  <Form.Group className="mb-3" controlId="inviteAcceptFirstName">
                    <Form.Label>First name</Form.Label>
                    <Form.Control
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      data-testid="rwm-invite-accept-firstname"
                      isInvalid={Boolean(fieldErrors.first_name)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="inviteAcceptLastName">
                    <Form.Label>Last name</Form.Label>
                    <Form.Control
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      data-testid="rwm-invite-accept-lastname"
                      isInvalid={Boolean(fieldErrors.last_name)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="inviteAcceptPassword">
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={() => setPasswordFocused(true)}
                      required
                      data-testid="rwm-invite-accept-password"
                      isInvalid={Boolean(fieldErrors.password)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="inviteAcceptConfirm">
                    <Form.Label>Confirm password</Form.Label>
                    <Form.Control
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      data-testid="rwm-invite-accept-confirm"
                    />
                  </Form.Group>

                  {showPasswordRequirements ? (
                    <ul className="small text-muted mb-3">
                      <li className={checks.length ? 'text-success' : ''}>At least 8 characters</li>
                      <li className={checks.upper ? 'text-success' : ''}>One uppercase letter</li>
                      <li className={checks.lower ? 'text-success' : ''}>One lowercase letter</li>
                      <li className={checks.number ? 'text-success' : ''}>One number</li>
                      <li className={checks.special ? 'text-success' : ''}>One special character</li>
                    </ul>
                  ) : null}

                  <Button
                    type="submit"
                    variant="info"
                    disabled={submitting}
                    data-testid="rwm-invite-accept-submit"
                  >
                    {submitting ? 'Activating…' : 'Activate account'}
                  </Button>
                </Form>
              </>
            ) : null}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}
